import { createApp } from "vue";
import VueClick from "vue-click";
import { createPinia } from "pinia";
import localforage from "localforage";
import VueClickAway from "vue3-click-away";
import { AppInsightsPlugin, type AppInsightsPluginOptions } from "vue3-application-insights";

import "@/index.scss";

import router from "@/router";
import { loadConfig, config } from "@/config";

import can from "@/directives/can";
import focus from "@/directives/focus";

import { routerPlugin } from "@/stores/plugins/router-plugin";
import { pollingPlugin } from "@/stores/plugins/polling-plugin";

import { setupAuthMachine } from "@/machines/auth-machine";

import { createInsightsSettings } from "@/services/insights";
import { reportReloadDiagnostics } from "@/services/ReloadDiagnostics";
import { handOffToActiveTab, listenForHandOffs } from "@/services/ViewerTabHandoff";

import App from "@/App.vue";

reportReloadDiagnostics();

const handedOff = handOffToActiveTab();
await loadConfig();

// Another viewer tab took this tab's agent link, so this one goes no further.
if (await handedOff) await new Promise<never>(() => undefined);

setupAuthMachine(router);

// Start our db drive
localforage.config({
  name: "EDX",
  driver: localforage.LOCALSTORAGE,
});

let aiOptions: AppInsightsPluginOptions | undefined = undefined;
// Setup Application Insights
if (!!config.instrumentationKey) {
  aiOptions = createInsightsSettings(config, router);
}

const nonceEl = document.querySelector("meta[name=nonce]");
const NONCE = nonceEl?.getAttribute("content") ?? "";
nonceEl?.remove();

const app = createApp(App)
  .use(router)
  .use(VueClickAway)
  .use(VueClick)
  .use(createPinia().use(routerPlugin(router)).use(pollingPlugin))
  .directive("can", can)
  .directive("focus", focus)
  .provide("NONCE", NONCE);

// Install the vue insights plugin if we are using telemetry
if (!!aiOptions) {
  app.use(AppInsightsPlugin, aiOptions);
}

if (import.meta.env.DEV) {
  app.config.errorHandler = (err, vm, info) => console.error("Vue error handler:", err, vm, info);
}

app.mount("#app");

listenForHandOffs(router);
